import { PrinterModelType } from "../api/visitor/devices/types"
import type { Asset, Desk, Room } from "../invites/types"
import type { HostResponse } from "../visitor_registration/types"
import { ISODate } from "src/types/sharedTypes"

export enum TabletConnectionType {
  PAIRED = "PAIRED",
  UNPAIRED = "UNPAIRED",
}

type Building = {
  id: string
  name: string
}

type ConnectedDevice = {
  id: string
  name: string
}

export type AuthenticateTabletRequest = {
  device_id: string
  pin: string
}

export type RegisterTabletRequest = {
  device_id: string
  model?: string
  version?: string
}

export type RegisterTabletResponse = {
  id: string
  device_id: string
  pin: string
  model: string
  version: string
  connection_status: TabletConnectionType
  building?: Building
}

export type BadgeResponse = {
  id: string
  name?: string
  mac_address: string
  battery: number | null
  status_sent_at: ISODate | null
}

/** Picture on PRINT_EPAPER_BADGE (API may send numeric `id`). */
export type PrintEpaperBadgeSocketPicture = {
  id: number | string
  name: string
  size?: number
  url?: string
}

export type PrintEpaperBadgeSocketVisitor = {
  id: string
  email: string
  full_name: string
  picture: PrintEpaperBadgeSocketPicture | null
}

/** Badge entry in PRINT_EPAPER_BADGE `data.badges` (no battery/status from socket). */
export type PrintEpaperBadgeSocketBadge = {
  id: string
  mac_address: string
  name?: string
}

export type PrintEpaperBadgeSocketField = {
  id: string
  value: string | null
}

/** WebSocket PRINT_EPAPER_BADGE `data`; must not be merged into visitor registration Redux. */
export type PrintEpaperBadgeSocketPayload = {
  id: string
  visitor: PrintEpaperBadgeSocketVisitor
  host: HostResponse | null
  checkin_at: string
  badges: PrintEpaperBadgeSocketBadge[]
  fields: PrintEpaperBadgeSocketField[]
  registration_id?: string
  desk?: Desk
  room?: Room
  assets?: Asset[]
}

/**
 * WebSocket EPAPER_BADGE_UPLOAD_IMAGE `data` (normalized in `uploadEpaperBadgeImageFromSocket`).
 * `image`: raw PNG base64 (528×768 portrait, palette-dithered). The normalizer also accepts
 * `badge_id`, `mac`, `image_base64`, nested `badge`, and `data:*;base64,...` URLs.
 */
export type UploadEpaperBadgeSocketPayload = {
  id: string
  mac_address: string
  name: string
  image: string
}

/** WebSocket EPAPER_BADGE_FIRMWARE_UPDATE `data` (before normalization). */
export type EpaperBadgeFirmwareUpdateSocketPayloadRaw = {
  badge: {
    id: string
    mac_address: string
    name?: string
  }
  fw_version: string
}

/** Normalized EPAPER_BADGE_FIRMWARE_UPDATE payload (after `normalizeEpaperBadgeFirmwareSocketData`). */
export type EpaperBadgeFirmwareUpdatePayload = {
  id: string
  mac_address: string
  fw_version: string
}

export type TabletResponse = {
  id: string
  name?: string
  model: string
  version: string
  building_id?: string
  battery_status: number
  battery_charging?: boolean
  status_sent_at?: string
  connected_device?: ConnectedDevice
  badges?: BadgeResponse[]
}

export type PrinterResponse = {
  id: string
  name?: string
  model: PrinterModelType
  ip_address: string
  building?: Building
  connected_device?: ConnectedDevice
}
